import athensLinesData from '@data/athens/lines.json'
import athensStopsData from '@data/athens/stops.json'
import athensRoutesData from '@data/athens/routes.json'
import { createOsethApi } from '../../../src/oseth-client'
import { detectCityFromHostname, getCityConfig, CITIES, getCurrentCity, isLandingPage, DEFAULT_CITY } from './city'
import type { CityId, CityConfig } from './city'

// Re-export city types and utilities
export type { CityId, CityConfig }
export { CITIES, getCurrentCity, detectCityFromHostname, isLandingPage }

// API base changes based on city
function getApiBase(): string {
  if (!clientConfig.cityId) return `${CITIES.athens.baseUrl}/api`
  if (clientConfig.cityId === 'athens') return `${CITIES.athens.baseUrl}/api`
  return `/api/${clientConfig.cityId}`
}

type DataSource = 'local' | 'api'

interface ClientConfig {
  dataSource: DataSource
  cityId: CityId | null
}

// Get initial city from hostname (null if landing page)
const initialCityId = detectCityFromHostname()

const defaultConfig: ClientConfig = {
  dataSource: 'local',
  cityId: initialCityId
}

let clientConfig = { ...defaultConfig }

export function configureClient(config: Partial<ClientConfig>) {
  clientConfig = { ...clientConfig, ...config }
}

export function getClientConfig(): ClientConfig {
  return { ...clientConfig }
}

export function getCurrentCityConfig(): CityConfig | null {
  if (!clientConfig.cityId) return null
  return getCityConfig(clientConfig.cityId)
}

// City-specific data getters
function getLinesData() {
  if (!clientConfig.cityId) return [] // Landing page has no lines
  return clientConfig.cityId === 'thessaloniki' ? [] : athensLinesData
}

function getStopsData() {
  if (!clientConfig.cityId) return { stops: [] }
  // Thessaloniki doesn't have stops data yet, return empty
  return clientConfig.cityId === 'thessaloniki' ? { stops: [] } : athensStopsData
}

export function getRoutesData() {
  if (!clientConfig.cityId) return { routes: [] }
  // Thessaloniki doesn't have routes data yet, return empty
  return clientConfig.cityId === 'thessaloniki' ? { routes: [] } : athensRoutesData
}

export interface LineInfo {
  LineCode: string
  LineID: string
  LineDescr: string
  LineDescrEng: string
}

export interface RouteInfo {
  RouteCode: string
  RouteDescr: string
  RouteDescrEng: string
  RouteType: string
  RouteDistance?: string
}

export interface StopInfo {
  StopCode: string
  StopDescr: string
  StopDescrEng: string
  StopLat: string
  StopLng: string
  RouteStopOrder: string
}

export interface BusLocation {
  VEH_NO: string
  CS_DATE: string
  CS_LAT: string
  CS_LNG: string
  ROUTE_CODE: string
}

export interface StopArrival {
  route_code: string
  btime2: string
  veh_code: string
}

export interface StopDetails {
  stop_code: string
  stop_descr: string
  stop_descr_eng: string
  stop_lat: string
  stop_lng: string
}

export interface RouteForStop {
  RouteCode: string
  RouteDescr: string
  RouteDescrEng: string
  LineCode: string
  LineID: string
  LineDescr: string
  LineDescrEng: string
}

export interface NearbyStop {
  StopCode: string
  StopID: string
  StopDescr: string
  StopDescrEng: string
  StopStreet: string
  StopStreetEng: string | null
  StopHeading: string
  StopLat: string
  StopLng: string
  distance: string
}

export interface AreaInfo {
  code: string
  name: string
  nameEng: string
  lat: number
  lng: number
}

export interface BusEta {
  vehicleNumber: string
  etaMinutes: number
  officialEtaMinutes?: number
  stopsAway: number
  currentStop: string
  distanceKm: number
  latitude?: number
  longitude?: number
}

export interface EtaResult {
  success: boolean
  stopName?: string
  stopCode?: string
  mapsUrl?: string
  nextBus?: BusEta
  approachingBuses?: BusEta[]
  passedBuses?: BusEta[]
  totalActive?: number
  error?: string
}

// Greek/Latin transliteration utilities
// NOTE: These are duplicated from src/greek-utils.ts to avoid importing Node.js dependencies
const GREEK_TO_LATIN: Record<string, string> = {
  'Α': 'A', 'Β': 'B', 'Γ': 'G', 'Δ': 'D', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Θ': 'TH',
  'Ι': 'I', 'Κ': 'K', 'Λ': 'L', 'Μ': 'M', 'Ν': 'N', 'Ξ': 'X', 'Ο': 'O', 'Π': 'P',
  'Ρ': 'R', 'Σ': 'S', 'Τ': 'T', 'Υ': 'Y', 'Φ': 'F', 'Χ': 'CH', 'Ψ': 'PS', 'Ω': 'O',
  'α': 'a', 'β': 'b', 'γ': 'g', 'δ': 'd', 'ε': 'e', 'ζ': 'z', 'η': 'h', 'θ': 'th',
  'ι': 'i', 'κ': 'k', 'λ': 'l', 'μ': 'm', 'ν': 'n', 'ξ': 'x', 'ο': 'o', 'π': 'p',
  'ρ': 'r', 'σ': 's', 'ς': 's', 'τ': 't', 'υ': 'y', 'φ': 'f', 'χ': 'ch', 'ψ': 'ps', 'ω': 'o'
}

const LATIN_TO_GREEK: Record<string, string> = {
  'A': 'Α', 'B': 'Β', 'G': 'Γ', 'D': 'Δ', 'E': 'Ε', 'Z': 'Ζ', 'H': 'Η', 'I': 'Ι',
  'K': 'Κ', 'L': 'Λ', 'M': 'Μ', 'N': 'Ν', 'X': 'Ξ', 'O': 'Ο', 'P': 'Π', 'R': 'Ρ',
  'S': 'Σ', 'T': 'Τ', 'Y': 'Υ', 'F': 'Φ', 'W': 'Ω'
}

export function greekToLatin(str: string): string {
  return str.split('').map(char => GREEK_TO_LATIN[char] || char).join('')
}

export function latinToGreek(str: string): string {
  return str.split('').map(char => LATIN_TO_GREEK[char.toUpperCase()] || char).join('')
}

export function normalizeForUrl(str: string): string {
  return greekToLatin(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function matchesLine(line: LineInfo, search: string): boolean {
  if (!search) return true
  const searchUpper = search.toUpperCase()
  const lineIdUpper = line.LineID.toUpperCase()
  const lineIdLatin = greekToLatin(lineIdUpper)
  const searchLatin = greekToLatin(searchUpper)

  if (lineIdUpper.includes(searchUpper) ||
      lineIdLatin.includes(searchUpper) ||
      lineIdLatin.includes(searchLatin) ||
      lineIdUpper.includes(latinToGreek(searchUpper))) {
    return true
  }

  const descr = (line.LineDescrEng || line.LineDescr || '').toUpperCase()
  const descrLatin = greekToLatin(descr)
  return descr.includes(searchUpper) ||
    descrLatin.includes(searchUpper) ||
    descrLatin.includes(searchLatin)
}

const API_TIMEOUT_MS = 10_000

async function fetchPath(path: string) {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS)

  try {
    const res = await fetch(path, { signal: controller.signal })
    if (!res.ok) {
      const status = [res.status, res.statusText].filter(Boolean).join(' ')
      throw new Error(`Bus data request failed (${status}).`)
    }

    const text = await res.text()
    if (!text) return null

    try {
      return JSON.parse(text)
    } catch {
      throw new Error('Bus data response was invalid.')
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Bus data request timed out. Try again.')
    }
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

async function fetchApi(endpoint: string) {
  return fetchPath(`${getApiBase()}/?${endpoint}`)
}

const osethApi = createOsethApi(path => fetchPath(`${getApiBase()}${path}`))

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const api = {
  getLines: async (): Promise<LineInfo[]> => {
    if (clientConfig.cityId === 'thessaloniki') {
      return osethApi.getLines()
    }
    if (clientConfig.dataSource === 'local') {
      return getLinesData() as LineInfo[]
    }
    return fetchApi('act=webGetLines')
  },

  getRoutes: (lineCode: string): Promise<RouteInfo[]> =>
    clientConfig.cityId === 'thessaloniki'
      ? osethApi.getRoutes(lineCode)
      : fetchApi(`act=webGetRoutes&p1=${lineCode}`),

  getStops: (routeCode: string): Promise<StopInfo[]> =>
    clientConfig.cityId === 'thessaloniki'
      ? osethApi.getStops(routeCode)
      : fetchApi(`act=webGetStops&p1=${routeCode}`),

  getLineBySlug: async (lineSlug: string): Promise<LineInfo | null> => {
    const lines = await api.getLines()
    return lines?.find(l => matchesLine(l, lineSlug)) || null
  },

  getFirstRouteForLine: async (lineSlug: string): Promise<RouteInfo | null> => {
    const line = await api.getLineBySlug(lineSlug)
    if (!line) return null
    const routes = await api.getRoutes(line.LineCode)
    return routes?.[0] || null
  },

  getBusLocations: (routeCode: string): Promise<BusLocation[]> =>
    clientConfig.cityId === 'thessaloniki'
      ? osethApi.getBusLocations(routeCode)
      : fetchPath(`/api/athens/locations?routeCode=${encodeURIComponent(routeCode)}`),

  getStopArrivals: (stopCode: string): Promise<StopArrival[]> =>
    clientConfig.cityId === 'thessaloniki'
      ? osethApi.getStopArrivals(stopCode)
      : fetchPath(`/api/athens/arrivals?stopCode=${encodeURIComponent(stopCode)}`),

  getEta: async (routeCode: string, stop: StopInfo): Promise<EtaResult> => {
    try {
      const [stopsData, busesData, arrivalsData] = await Promise.all([
        api.getStops(routeCode),
        api.getBusLocations(routeCode),
        api.getStopArrivals(stop.StopCode)
      ])

      const validStops = Array.isArray(stopsData)
        ? stopsData.filter(routeStop => {
            const lat = parseFloat(routeStop.StopLat)
            const lng = parseFloat(routeStop.StopLng)
            const order = parseInt(routeStop.RouteStopOrder)
            return Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(order)
          })
        : []

      if (validStops.length === 0) {
        return { success: false, error: 'No stops are available for this route.' }
      }

      const officialEtaByVehicle = new Map<string, number>()
      if (Array.isArray(arrivalsData)) {
        for (const arrival of arrivalsData) {
          if (arrival.route_code === routeCode) {
            officialEtaByVehicle.set(arrival.veh_code, parseInt(arrival.btime2))
          }
        }
      }

      const stopLat = parseFloat(stop.StopLat)
      const stopLng = parseFloat(stop.StopLng)
      const targetOrder = parseInt(stop.RouteStopOrder)

      if (!Number.isFinite(stopLat) || !Number.isFinite(stopLng) || !Number.isFinite(targetOrder)) {
        return { success: false, error: 'The selected stop has invalid location data.' }
      }

      const validBuses = Array.isArray(busesData)
        ? busesData.filter(bus => (
            Number.isFinite(parseFloat(bus.CS_LAT)) &&
            Number.isFinite(parseFloat(bus.CS_LNG))
          ))
        : []

      if (validBuses.length === 0) {
        return {
          success: true,
          stopName: stop.StopDescrEng || stop.StopDescr,
          stopCode: stop.StopCode,
          mapsUrl: `https://www.google.com/maps/search/?api=1&query=${stopLat},${stopLng}`,
          approachingBuses: [],
          totalActive: 0
        }
      }

      const approaching: BusEta[] = []
      const passed: BusEta[] = []
      for (const bus of validBuses) {
        const busLat = parseFloat(bus.CS_LAT)
        const busLng = parseFloat(bus.CS_LNG)
        const dist = calculateDistance(busLat, busLng, stopLat, stopLng)

        let closestStop = validStops[0], minDist = Infinity
        for (const s of validStops) {
          const d = calculateDistance(busLat, busLng, parseFloat(s.StopLat), parseFloat(s.StopLng))
          if (d < minDist) { minDist = d; closestStop = s }
        }

        if (minDist > 2) continue

        const busOrder = parseInt(closestStop.RouteStopOrder)
        const busInfo: BusEta = {
          vehicleNumber: bus.VEH_NO,
          etaMinutes: Math.round((dist / 15) * 60),
          officialEtaMinutes: officialEtaByVehicle.get(bus.VEH_NO),
          stopsAway: Math.abs(targetOrder - busOrder),
          currentStop: closestStop.StopDescrEng || closestStop.StopDescr,
          distanceKm: Math.round(dist * 100) / 100,
          latitude: busLat,
          longitude: busLng
        }
        
        if (officialEtaByVehicle.has(bus.VEH_NO) || busOrder <= targetOrder) {
          approaching.push(busInfo)
        } else {
          passed.push(busInfo)
        }
      }

      approaching.sort((a, b) => a.stopsAway - b.stopsAway)
      passed.sort((a, b) => a.stopsAway - b.stopsAway)

      return {
        success: true,
        stopName: stop.StopDescrEng || stop.StopDescr,
        stopCode: stop.StopCode,
        mapsUrl: `https://www.google.com/maps/search/?api=1&query=${stopLat},${stopLng}`,
        nextBus: approaching[0],
        approachingBuses: approaching,
        passedBuses: passed,
        totalActive: approaching.length + passed.length
      }
    } catch (e: any) {
      return { success: false, error: e.message }
    }
  },

  findStopByCode: async (routeCode: string, stopCode: string): Promise<StopInfo | null> => {
    const stops = await api.getStops(routeCode)
    return stops?.find(s => s.StopCode === stopCode) || null
  },

  getStopDetails: async (stopCode: string): Promise<StopDetails | null> => {
    if (clientConfig.cityId === 'thessaloniki') {
      return osethApi.getStopDetails(stopCode)
    }
    // Try local data first
    const stopsData = getStopsData()
    const localStop = (stopsData as any).stops?.find((s: any) => s.StopCode === stopCode)
    if (localStop) {
      return {
        stop_code: localStop.StopCode,
        stop_descr: localStop.StopDescr,
        stop_descr_eng: localStop.StopDescrEng || localStop.StopDescr,
        stop_lat: localStop.StopLat,
        stop_lng: localStop.StopLng
      }
    }
    
    return fetchApi(`act=getStopNameAndXY&p1=${stopCode}`)
  },

  getRoutesForStop: async (stopCode: string): Promise<RouteForStop[]> => {
    if (clientConfig.cityId === 'thessaloniki') {
      return osethApi.getRoutesForStop(stopCode)
    }
    // Search local routes data for routes that include this stop
    const routesData = getRoutesData()
    const linesData = getLinesData()
    const routes = (routesData as any).routes || []
    const lines = linesData as LineInfo[]
    
    const matchingRoutes: RouteForStop[] = []
    
    for (const route of routes) {
      const hasStop = route.stops?.some((s: any) => s.stopCode === stopCode)
      if (hasStop) {
        const line = lines.find(l => l.LineCode === route.lineCode)
        matchingRoutes.push({
          RouteCode: route.routeCode,
          RouteDescr: route.routeDescr,
          RouteDescrEng: route.routeDescrEng || route.routeDescr,
          LineCode: route.lineCode,
          LineID: route.lineId || line?.LineID || '',
          LineDescr: line?.LineDescr || route.routeDescr,
          LineDescrEng: line?.LineDescrEng || route.routeDescrEng || ''
        })
      }
    }
    
    if (matchingRoutes.length > 0) {
      return matchingRoutes
    }
    
    const result = await fetchApi(`act=webRoutesForStop&p1=${stopCode}`)
    return result || []
  },

  // `distance` is always returned in metres (the UI renders it as "Xm").
  // OASA returns degrees and OASTH returns nothing, so compute it here.
  getClosestStops: async (lat: number, lng: number): Promise<NearbyStop[]> => {
    const stops: Omit<NearbyStop, 'distance'>[] = clientConfig.cityId === 'thessaloniki'
      ? await osethApi.getClosestStops(lat, lng)
      : await fetchPath(`/api/athens/closest?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`)
    return (Array.isArray(stops) ? stops : [])
      .map(stop => ({
        ...stop,
        distance: String(Math.round(calculateDistance(lat, lng, Number(stop.StopLat), Number(stop.StopLng)) * 1000)),
      }))
      .sort((a, b) => Number(a.distance) - Number(b.distance))
  }
}

const ATHENS_AREAS: AreaInfo[] = [
  { code: 'alimos', name: 'Άλιμος', nameEng: 'Alimos', lat: 37.9115, lng: 23.7150 },
  { code: 'glyfada', name: 'Γλυφάδα', nameEng: 'Glyfada', lat: 37.8600, lng: 23.7533 },
  { code: 'vouliagmeni', name: 'Βουλιαγμένη', nameEng: 'Vouliagmeni', lat: 37.8100, lng: 23.7800 },
  { code: 'piraeus', name: 'Πειραιάς', nameEng: 'Piraeus', lat: 37.9475, lng: 23.6415 },
  { code: 'kifisia', name: 'Κηφισιά', nameEng: 'Kifisia', lat: 38.0747, lng: 23.8108 },
  { code: 'marousi', name: 'Μαρούσι', nameEng: 'Marousi', lat: 38.0505, lng: 23.8063 },
  { code: 'chalandri', name: 'Χαλάνδρι', nameEng: 'Chalandri', lat: 38.0214, lng: 23.7986 },
  { code: 'nea-smyrni', name: 'Νέα Σμύρνη', nameEng: 'Nea Smyrni', lat: 37.9447, lng: 23.7133 },
  { code: 'kallithea', name: 'Καλλιθέα', nameEng: 'Kallithea', lat: 37.9561, lng: 23.6986 },
  { code: 'pagrati', name: 'Παγκράτι', nameEng: 'Pagrati', lat: 37.9683, lng: 23.7514 },
  { code: 'syntagma', name: 'Σύνταγμα', nameEng: 'Syntagma', lat: 37.9755, lng: 23.7348 },
  { code: 'monastiraki', name: 'Μοναστηράκι', nameEng: 'Monastiraki', lat: 37.9763, lng: 23.7258 },
  { code: 'omonoia', name: 'Ομόνοια', nameEng: 'Omonoia', lat: 37.9839, lng: 23.7275 },
  { code: 'peristeri', name: 'Περιστέρι', nameEng: 'Peristeri', lat: 38.0167, lng: 23.6917 },
  { code: 'ilioupoli', name: 'Ηλιούπολη', nameEng: 'Ilioupoli', lat: 37.9300, lng: 23.7650 },
]

const THESSALONIKI_AREAS: AreaInfo[] = [
  { code: 'kentro', name: 'Κέντρο', nameEng: 'Center', lat: 40.6401, lng: 22.9444 },
  { code: 'aristotelous', name: 'Αριστοτέλους', nameEng: 'Aristotelous', lat: 40.6326, lng: 22.9411 },
  { code: 'kalamaria', name: 'Καλαμαριά', nameEng: 'Kalamaria', lat: 40.5857, lng: 22.9532 },
  { code: 'toumpa', name: 'Τούμπα', nameEng: 'Toumba', lat: 40.6195, lng: 22.9714 },
  { code: 'stavroupoli', name: 'Σταυρούπολη', nameEng: 'Stavroupoli', lat: 40.6652, lng: 22.9213 },
  { code: 'evosmos', name: 'Εύοσμος', nameEng: 'Evosmos', lat: 40.6766, lng: 22.9019 },
  { code: 'pylaia', name: 'Πυλαία', nameEng: 'Pylaia', lat: 40.5950, lng: 22.9900 },
  { code: 'thermi', name: 'Θέρμη', nameEng: 'Thermi', lat: 40.5475, lng: 23.0156 },
  { code: 'panorama', name: 'Πανόραμα', nameEng: 'Panorama', lat: 40.5933, lng: 23.0367 },
  { code: 'neo-sidiroporto', name: 'Νέος Σιδηροδρομικός', nameEng: 'New Railway Station', lat: 40.6553, lng: 22.9019 },
  { code: 'ktel', name: 'ΚΤΕΛ', nameEng: 'KTEL Bus Station', lat: 40.6553, lng: 22.9019 },
  { code: 'airport', name: 'Αεροδρόμιο', nameEng: 'Airport', lat: 40.5197, lng: 22.9709 },
]

// Export areas based on current city
export function getAreas(): AreaInfo[] {
  return clientConfig.cityId === 'thessaloniki' ? THESSALONIKI_AREAS : ATHENS_AREAS
}

// Legacy export for backward compatibility (defaults to Athens)
export const AREAS: AreaInfo[] = ATHENS_AREAS
